import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Image as ImageIcon } from "@mui/icons-material";
import {
  useGetDataset,
  useGetSourceDataRecord,
} from "src/views/datasets/services/DatasetServices";

import DatasetTypeChip from "src/views/datasets/components/DatasetTypeChip";
import {
  isImageSource,
  getImageContentTypeLabel,
} from "../../utils/imageSourceUtils";
import { getSourceDisplayName } from "../../utils/sourceDisplayName";
import type { ChunkMetadata, ChunkData } from "../../types/citations";
import SourceImageThumbnail from "./SourceImageThumbnail";
import { useOnboardingStore } from "../../stores/OnboardingStore";
import { reportOnboardingEvent } from "../../services/OnboardingService";

export type DataChunk = {
  id: number;
  source_data_record_fk: number;
  chunk_data: ChunkData & {
    data?: any;
  };
  chunk_strategy_fk?: number;
  chunk_metadata: ChunkMetadata & {
    page_number?: number[];
  };
};

export type SearchResult = {
  source_data_record: any;
  dataset: any;
  data_chunk: DataChunk;
};

type SourceListItemProps = {
  source: DataChunk;
  onSourceClick: (sdrId: number | null, sourceId: number | number) => void;
  index: number;
};

const SourceListItem = ({
  source,
  onSourceClick,
  index,
}: SourceListItemProps) => {
  const theme = useTheme();
  const { data: sourceDataRecord, isLoading: isSourceDataRecordLoading } =
    useGetSourceDataRecord(source?.source_data_record_fk);
  const datasetId = sourceDataRecord?.dataset_fk;
  const isDocument = Boolean(sourceDataRecord?.data?.file_name);

  const { data: dataset } = useGetDataset(datasetId);
  const datasetType = dataset?.dataset_type?.name;

  const pageNumber = source?.chunk_metadata?.page_number;

  // Image source detection
  const isImage = isImageSource(source?.chunk_metadata);
  const imageArtifactId = source?.chunk_metadata?.image_artifact_id;
  const imageContentType = source?.chunk_data?.content_type;

  const handleClick = () => {
    // Mirror the inline-citation pill telemetry so Sources-tab clicks count
    // toward the FTUX citation_clicked cohort too.
    const { isOnboarding, onboardingDialogueId } =
      useOnboardingStore.getState();
    if (isOnboarding) {
      reportOnboardingEvent("citation_clicked", {
        source: "sources_tab",
        source_data_record_fk: source?.source_data_record_fk,
        source_id: source.id,
        index,
        dialogue_id: onboardingDialogueId,
      }).catch(() => {
        // Best-effort telemetry — never block UI.
      });
    }
    onSourceClick(source?.source_data_record_fk, source.id);
  };

  if (isSourceDataRecordLoading) {
    return (
      <Card
        sx={{
          mb: 1.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ p: 2 }}>
          <Skeleton
            variant="rectangular"
            animation="wave"
            width="100%"
            height={60}
          />
        </Box>
      </Card>
    );
  }

  return (
    <Card
      data-testid={`aa-src-item-${index}`}
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 2px 8px ${theme.palette.primary.main}20`,
          transform: "translateY(-1px)",
        },
      }}
    >
      <CardActionArea
        onClick={handleClick}
        data-testid={`aa-src-item-action-${index}`}
        data-attr={`assistant:card:view-source-${index}`}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                label={index + 1}
                size="small"
                variant="outlined"
                sx={{
                  minWidth: "28px",
                  height: "24px",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                }}
              />
              {isImage ? (
                <Chip
                  icon={<ImageIcon sx={{ fontSize: "1rem" }} />}
                  label={getImageContentTypeLabel(imageContentType)}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={{
                    height: "24px",
                    fontSize: "0.7rem",
                  }}
                />
              ) : (
                <Box sx={{ minWidth: 32 }}>
                  <DatasetTypeChip type={datasetType} avatar />
                </Box>
              )}
            </Stack>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  lineHeight: 1.3,
                }}
              >
                {dataset?.name}
              </Typography>

              {/* Image source display */}
              {isImage && imageArtifactId && (
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <SourceImageThumbnail
                    imageArtifactId={imageArtifactId}
                    size="small"
                  />
                  <Stack spacing={0.25} sx={{ flex: 1 }}>
                    {sourceDataRecord?.data?.file_name && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.primary",
                          fontWeight: 500,
                          lineHeight: 1.4,
                        }}
                      >
                        {sourceDataRecord.data.file_name}
                      </Typography>
                    )}
                    {pageNumber && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          lineHeight: 1.3,
                        }}
                      >
                        Page:{" "}
                        {Array.isArray(pageNumber)
                          ? pageNumber.join(", ")
                          : pageNumber}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              )}

              {/* Non-image document source display */}
              {!isImage && !isDocument && sourceDataRecord && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.4,
                  }}
                >
                  Source ID: {sourceDataRecord?.source_uid}
                </Typography>
              )}

              {!isImage && isDocument && sourceDataRecord && (
                <Stack spacing={0.25}>
                  <Typography
                    variant="body2"
                    title={sourceDataRecord?.data?.file_name}
                    sx={{
                      color: "text.primary",
                      fontWeight: 500,
                      lineHeight: 1.4,
                    }}
                  >
                    {getSourceDisplayName(sourceDataRecord?.data)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.3,
                    }}
                  >
                    Page(s):{" "}
                    {Array.isArray(pageNumber)
                      ? pageNumber.join(", ")
                      : pageNumber}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>
        </Box>
      </CardActionArea>
    </Card>
  );
};

export default SourceListItem;
